import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { FixedTimestep } from '../engine/FixedTimestep';
import { CollisionGrid } from '../engine/CollisionGrid';
import { Player } from '../entities/Player';
import { Collectible, CollectibleType } from '../entities/Collectible';
import { Enemy } from '../entities/Enemy';
import { gameAPI, SaveData } from '../api/GameAPI';

// Tile types for level design
const _: number = 0;  // Empty
const G: number = 1;  // Ground
const P: number = 2;  // Platform
const W: number = 3;  // Wall

/**
 * Main gameplay scene with sprites, collectibles, and enemies
 */
export class GameScene extends Phaser.Scene {
  private timestep!: FixedTimestep;
  private collisionGrid!: CollisionGrid;
  private player!: Player;
  private collectibles: Collectible[] = [];
  private enemies: Enemy[] = [];

  // Input
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private jumpKey!: Phaser.Input.Keyboard.Key;
  private pogoKey!: Phaser.Input.Keyboard.Key;
  private jumpPressed: boolean = false;
  private prevJump: boolean = false;

  // UI
  private scoreText!: Phaser.GameObjects.Text;
  private livesText!: Phaser.GameObjects.Text;
  private ammoText!: Phaser.GameObjects.Text;

  // Level data
  private levelData: number[][] = [];
  private tileSprites: Phaser.GameObjects.Sprite[] = [];

  // Game state
  private currentLevel: number = 1;
  private playTime: number = 0;
  private isPaused: boolean = false;
  private pauseMenu: Phaser.GameObjects.Container | null = null;
  private pauseMenuIndex: number = 0;
  private collectedItemIds: string[] = [];
  private escKey!: Phaser.Input.Keyboard.Key;
  private loadedSave: SaveData | null = null;

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data?: { loadSave?: SaveData }): void {
    this.loadedSave = data?.loadSave || null;
  }

  create(): void {
    // Initialize systems
    this.timestep = new FixedTimestep();
    this.collisionGrid = new CollisionGrid();
    this.player = new Player();
    this.collectibles = [];
    this.enemies = [];
    this.tileSprites = [];
    this.collectedItemIds = [];
    this.isPaused = false;
    this.pauseMenu = null;
    this.playTime = 0;

    // Create level
    this.createLevel();

    // Initialize player
    this.player.init(this, this.collisionGrid, 32, 130);

    // Create collectibles
    this.createCollectibles();

    // Create enemies
    this.createEnemies();

    // Setup input
    this.setupInput();

    // Setup timestep
    this.timestep.onFixedUpdate((dt) => this.fixedUpdate(dt));
    this.timestep.onRender((alpha) => this.renderInterpolated(alpha));

    // Create UI
    this.createUI();

    // Set background
    this.cameras.main.setBackgroundColor(0x1a1a2e);

    // Setup ESC for pause
    if (this.input.keyboard) {
      this.escKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    }

    // Load save data if provided
    if (this.loadedSave) {
      this.loadSaveData(this.loadedSave);
    }
  }

  private loadSaveData(save: SaveData): void {
    this.currentLevel = save.level;
    this.playTime = save.play_time;
    this.player.setPosition(save.position_x, save.position_y);

    // Set player stats
    for (let i = 0; i < save.score; i += 100) {
      this.player.addScore(100);
    }

    // Set keys
    if (save.keys_collected.yellow) this.player.addKey('yellow');
    if (save.keys_collected.red) this.player.addKey('red');
    if (save.keys_collected.blue) this.player.addKey('blue');

    // Mark collected items
    this.collectedItemIds = save.collected_items || [];
    for (const collectible of this.collectibles) {
      const itemId = `${collectible.getType()}_${collectible.getHitbox().x}_${collectible.getHitbox().y}`;
      if (this.collectedItemIds.includes(itemId)) {
        collectible.collect();
      }
    }
  }

  private createLevel(): void {
    // Level design (20x12 tiles)
    // _=empty, G=ground, P=platform, W=wall
    this.levelData = [
      [W,W,W,W,W,W,W,W,W,W,W,W,W,W,W,W,W,W,W,W],
      [W,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,W],
      [W,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,W],
      [W,_,_,_,_,_,_,_,_,_,_,P,P,P,_,_,_,_,_,W],
      [W,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,W],
      [W,_,_,_,_,P,P,P,P,_,_,_,_,_,_,_,P,P,_,W],
      [W,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,W],
      [W,_,_,P,P,_,_,_,_,_,_,_,_,P,P,P,_,_,_,W],
      [W,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,W],
      [W,_,G,G,_,_,_,_,_,_,G,G,G,_,_,_,_,G,G,W],
      [G,G,G,G,G,G,G,G,G,G,G,G,G,G,G,G,G,G,G,G],
      [G,G,G,G,G,G,G,G,G,G,G,G,G,G,G,G,G,G,G,G],
    ];

    // Convert to collision grid (1 = solid, 0 = empty)
    const collisionData = this.levelData.map(row =>
      row.map(tile => tile === _ ? 0 : 1)
    );
    this.collisionGrid.loadGrid(collisionData);

    // Create tile sprites
    const tileSize = GAME_CONFIG.TILE_SIZE;
    for (let y = 0; y < this.levelData.length; y++) {
      for (let x = 0; x < this.levelData[y].length; x++) {
        const tile = this.levelData[y][x];
        if (tile === _) continue;

        let textureName = 'tileGround';
        if (tile === P) textureName = 'tilePlatform';
        else if (tile === W) textureName = 'tileWall';

        const sprite = this.add.sprite(x * tileSize, y * tileSize, textureName);
        sprite.setOrigin(0, 0);
        this.tileSprites.push(sprite);
      }
    }
  }

  private createCollectibles(): void {
    const items: { type: CollectibleType; x: number; y: number }[] = [
      // Coins scattered around
      { type: 'coin', x: 50, y: 130 },
      { type: 'coin', x: 70, y: 130 },
      { type: 'coin', x: 90, y: 100 },
      { type: 'coin', x: 110, y: 70 },
      { type: 'coin', x: 180, y: 45 },
      { type: 'coin', x: 200, y: 45 },
      { type: 'coin', x: 85, y: 85 },
      { type: 'coin', x: 220, y: 100 },
      { type: 'coin', x: 270, y: 70 },

      // Floppy disk (secret)
      { type: 'floppy', x: 270, y: 130 },

      // Keys
      { type: 'keyYellow', x: 130, y: 70 },

      // Ammo
      { type: 'ammo', x: 220, y: 70 },
      { type: 'ammo', x: 50, y: 100 },
    ];

    for (const item of items) {
      const collectible = new Collectible(item);
      collectible.init(this);
      this.collectibles.push(collectible);
    }
  }

  private createEnemies(): void {
    const enemyData = [
      { type: 'slugger' as const, x: 100, y: 140, patrolLeft: 70, patrolRight: 140 },
      { type: 'hopper' as const, x: 200, y: 140, patrolLeft: 180, patrolRight: 240 },
      { type: 'slugger' as const, x: 90, y: 55, patrolLeft: 80, patrolRight: 115 },
    ];

    for (const data of enemyData) {
      const enemy = new Enemy(data);
      enemy.init(this, this.collisionGrid);
      this.enemies.push(enemy);
    }
  }

  private setupInput(): void {
    if (!this.input.keyboard) return;

    this.cursors = this.input.keyboard.createCursorKeys();
    this.jumpKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.pogoKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);
  }

  private createUI(): void {
    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#ffffff',
      backgroundColor: '#00000088',
      padding: { x: 4, y: 2 },
    };

    // Top bar
    this.scoreText = this.add.text(4, 4, 'SCORE: 0', style);
    this.livesText = this.add.text(100, 4, 'LIVES: 3', style);
    this.ammoText = this.add.text(170, 4, 'AMMO: 10', style);

    // Title
    this.add.text(240, 4, 'COSMIC CADET', {
      ...style,
      color: '#00ff88',
    });

    // Controls hint
    this.add.text(4, GAME_CONFIG.HEIGHT - 12, 'ARROWS:Move SPACE:Jump SHIFT:Pogo', {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: '#888888',
    });
  }

  update(time: number, delta: number): void {
    // Check for pause
    if (Phaser.Input.Keyboard.JustDown(this.escKey)) {
      this.togglePause();
    }

    if (this.isPaused) {
      return;
    }

    // Track play time
    this.playTime += delta / 1000;

    this.processInput();
    this.timestep.update(time, delta);

    // Update collectibles (bobbing animation)
    for (const collectible of this.collectibles) {
      collectible.update(time);
    }

    // Update UI
    this.updateUI();
  }

  private togglePause(): void {
    if (this.isPaused) {
      this.resumeGame();
    } else {
      this.pauseGame();
    }
  }

  private pauseGame(): void {
    this.isPaused = true;
    this.pauseMenuIndex = 0;

    // Create pause overlay
    this.pauseMenu = this.add.container(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2);

    // Background
    const bg = this.add.rectangle(0, 0, 120, 100, 0x000000, 0.8);
    this.pauseMenu.add(bg);

    // Title
    const title = this.add.text(0, -35, 'PAUSED', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffffff',
    });
    title.setOrigin(0.5);
    this.pauseMenu.add(title);

    // Menu items
    const items = ['Resume', 'Save Game', 'Main Menu'];
    items.forEach((text, i) => {
      const item = this.add.text(0, -10 + i * 18, text, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: i === this.pauseMenuIndex ? '#00ff88' : '#ffffff',
      });
      item.setOrigin(0.5);
      item.setName(`pause_item_${i}`);
      this.pauseMenu!.add(item);
    });

    // Setup pause menu input
    if (this.input.keyboard) {
      this.input.keyboard.on('keydown-UP', this.pauseMenuUp, this);
      this.input.keyboard.on('keydown-DOWN', this.pauseMenuDown, this);
      this.input.keyboard.on('keydown-ENTER', this.pauseMenuSelect, this);
      this.input.keyboard.on('keydown-SPACE', this.pauseMenuSelect, this);
    }
  }

  private resumeGame(): void {
    this.isPaused = false;
    if (this.pauseMenu) {
      this.pauseMenu.destroy();
      this.pauseMenu = null;
    }
    if (this.input.keyboard) {
      this.input.keyboard.off('keydown-UP', this.pauseMenuUp, this);
      this.input.keyboard.off('keydown-DOWN', this.pauseMenuDown, this);
      this.input.keyboard.off('keydown-ENTER', this.pauseMenuSelect, this);
      this.input.keyboard.off('keydown-SPACE', this.pauseMenuSelect, this);
    }
  }

  private pauseMenuUp(): void {
    this.pauseMenuIndex = Math.max(0, this.pauseMenuIndex - 1);
    this.updatePauseMenu();
  }

  private pauseMenuDown(): void {
    this.pauseMenuIndex = Math.min(2, this.pauseMenuIndex + 1);
    this.updatePauseMenu();
  }

  private updatePauseMenu(): void {
    if (!this.pauseMenu) return;
    for (let i = 0; i < 3; i++) {
      const item = this.pauseMenu.getByName(`pause_item_${i}`) as Phaser.GameObjects.Text;
      if (item) {
        item.setColor(i === this.pauseMenuIndex ? '#00ff88' : '#ffffff');
      }
    }
  }

  private async pauseMenuSelect(): Promise<void> {
    switch (this.pauseMenuIndex) {
      case 0: // Resume
        this.resumeGame();
        break;
      case 1: // Save Game
        await this.saveCurrentGame();
        break;
      case 2: // Main Menu
        this.resumeGame();
        this.scene.start('MenuScene');
        break;
    }
  }

  private async saveCurrentGame(): Promise<void> {
    if (!gameAPI.getUsername()) {
      console.log('Not registered, cannot save');
      return;
    }

    const playerPos = this.player.getPosition();
    const saveData: Omit<SaveData, 'updated_at'> = {
      slot: 0,
      level: this.currentLevel,
      score: this.player.getScore(),
      lives: this.player.getLives(),
      ammo: this.player.getAmmo(),
      keys_collected: {
        yellow: this.player.hasKey('yellow'),
        red: this.player.hasKey('red'),
        blue: this.player.hasKey('blue'),
      },
      position_x: playerPos.x,
      position_y: playerPos.y,
      collected_items: this.collectedItemIds,
      play_time: Math.floor(this.playTime),
    };

    try {
      await gameAPI.saveGame(saveData);
      console.log('Game saved!');
      // Show save confirmation
      const savedText = this.add.text(GAME_CONFIG.WIDTH / 2, 60, 'SAVED!', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#00ff88',
        backgroundColor: '#000000',
        padding: { x: 8, y: 4 },
      });
      savedText.setOrigin(0.5);
      this.time.delayedCall(1500, () => savedText.destroy());
    } catch (error) {
      console.error('Save failed:', error);
    }
  }

  private processInput(): void {
    if (!this.input.keyboard) return;

    const left = this.cursors.left.isDown || this.input.keyboard.checkDown(this.input.keyboard.addKey('A'));
    const right = this.cursors.right.isDown || this.input.keyboard.checkDown(this.input.keyboard.addKey('D'));
    const jump = this.jumpKey.isDown || this.cursors.up.isDown;
    const pogo = this.pogoKey.isDown;

    this.jumpPressed = jump && !this.prevJump;
    this.prevJump = jump;

    this.player.setInput(left, right, jump, this.jumpPressed, pogo);
  }

  private fixedUpdate(dt: number): void {
    // Update player
    this.player.fixedUpdate(dt);

    // Update enemies
    const playerPos = this.player.getPosition();
    for (const enemy of this.enemies) {
      if (!enemy.isDead()) {
        enemy.fixedUpdate(dt, playerPos.x, playerPos.y);
      }
    }

    // Check collectible collisions
    this.checkCollectibleCollisions();

    // Check enemy collisions
    this.checkEnemyCollisions();
  }

  private checkCollectibleCollisions(): void {
    const playerBox = this.player.getHitbox();

    for (const collectible of this.collectibles) {
      if (collectible.isCollected()) continue;

      const itemBox = collectible.getHitbox();
      if (this.aabbOverlap(playerBox, itemBox)) {
        const result = collectible.collect();
        if (result) {
          // Track collected item
          const itemId = `${result.type}_${itemBox.x}_${itemBox.y}`;
          this.collectedItemIds.push(itemId);

          this.handleCollect(result.type, result.value);
        }
      }
    }
  }

  private handleCollect(type: CollectibleType, value: number): void {
    switch (type) {
      case 'coin':
      case 'floppy':
        this.player.addScore(value);
        break;
      case 'ammo':
        this.player.addAmmo(value);
        break;
      case 'keyYellow':
        this.player.addKey('yellow');
        break;
      case 'keyRed':
        this.player.addKey('red');
        break;
      case 'keyBlue':
        this.player.addKey('blue');
        break;
    }
  }

  private checkEnemyCollisions(): void {
    const playerBox = this.player.getHitbox();

    for (const enemy of this.enemies) {
      if (enemy.isDead()) continue;

      const enemyBox = enemy.getHitbox();
      if (this.aabbOverlap(playerBox, enemyBox)) {
        // For now, just kill the enemy on contact
        // In full game: check if player is above (stomp) or take damage
        enemy.hit();
        this.player.addScore(200);
      }
    }
  }

  private aabbOverlap(a: { x: number; y: number; width: number; height: number },
                      b: { x: number; y: number; width: number; height: number }): boolean {
    return (
      a.x < b.x + b.width &&
      a.x + a.width > b.x &&
      a.y < b.y + b.height &&
      a.y + a.height > b.y
    );
  }

  private renderInterpolated(alpha: number): void {
    this.player.render(alpha);

    for (const enemy of this.enemies) {
      enemy.render(alpha);
    }
  }

  private updateUI(): void {
    this.scoreText.setText(`SCORE: ${this.player.getScore()}`);
    this.livesText.setText(`LIVES: ${this.player.getLives()}`);
    this.ammoText.setText(`AMMO: ${this.player.getAmmo()}`);
  }
}
